import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { forceRefresh, getValidAccessToken } from 'services/auth';
import { uploadFile } from 'services/tus/uploadFile';
import useAlert from 'utils/alert';
import { asyncForEach } from 'utils/helpers';
import { DocumentsType } from 'v2-types/order';

import { DocumentPartyUpload } from './types';

type UseManualDocumentUploadModalParams = {
  document: DocumentsType;
  onUpdateScuccess: () => void;
};

export const useManualDocumentUploadModal = ({
  document,
  onUpdateScuccess,
}: UseManualDocumentUploadModalParams) => {
  const { t } = useTranslation();
  const { id: orderId } = useParams<{ id: string }>();
  const Alert = useAlert();
  const queryClient = useQueryClient();
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedParty, setSelectedParty] = useState<string>('');
  const {
    getInputProps,
    getRootProps,
    open: openFileDialog,
  } = useDropzone({
    maxSize: 31457280,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.oasis.opendocument.text': ['.odt'],
      'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
    },
    noClick: true,
    noKeyboard: true,
    onDropAccepted: (files: File[]) => setFilesToUpload([...filesToUpload, ...files]),
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid },
    reset: resetForm,
    setValue,
  } = useForm<DocumentPartyUpload>({ mode: 'onChange' });

  const onRemoveFile = ({ file }: { file: File }) => {
    setFilesToUpload((prevFilesToUpload) =>
      prevFilesToUpload.filter((fileToUpload) => fileToUpload.name !== file.name)
    );
  };

  const onUploadDocuments = handleSubmit(async (data) => {
    setIsLoading(true);
    let filesUploadedCount = 0;
    await asyncForEach(filesToUpload, async (file) => {
      try {
        await uploadFile({
          file,
          archiveId: document._id,
          getToken: getValidAccessToken,
          onUnauthorized: async (failedToken: string) => {
            await forceRefresh(failedToken);
            return true;
          },
          orderId,
          partyId: data.partyId,
          filename: file.name,
        });
        filesUploadedCount += 1;
      } catch (error) {
        Alert(t('documents:error-uplading'), 'error');
      }
    });
    setIsLoading(false);

    if (filesUploadedCount === filesToUpload.length) {
      Alert(t('documents:upload-success'), 'success');
      resetForm();
      setFilesToUpload([]);
      queryClient.invalidateQueries(['order-documents', orderId]);
      onUpdateScuccess();
    }
  });

  return {
    t,
    control,
    watch,
    isValid,
    onRemoveFile,
    onUploadDocuments,
    openFileDialog,
    getInputProps,
    getRootProps,
    filesToUpload,
    isLoading,
    selectedParty,
    setSelectedParty,
    setValue,
  };
};
